import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import { siteConfig } from '@/mocks/siteConfig';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'darknet-basics');

  const currentCat = categories.find((c) => c.slug === activeSlug) || categories[0];
  const categoryArticles = getArticlesByCategory(activeSlug);

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: currentCat.name },
  ];

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCat.name} - ${siteConfig.name}`,
    description: currentCat.description,
    url: `${siteConfig.url}/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteConfig.name,
      url: siteConfig.url,
    },
  };

  return (
    <>
      <SEO
        title={currentCat.name}
        description={currentCat.description}
        keywords={`${currentCat.name},暗网安全,网络安全,51暗网`}
        canonicalUrl={`${siteConfig.url}/category/${activeSlug}`}
        schema={categorySchema}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <section className="py-14 md:py-20 bg-background-100/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-3">
              <Breadcrumb items={breadcrumbItems} />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-3">{currentCat.name}</h1>
            <p className="text-base text-foreground-600 max-w-2xl mb-8">{currentCat.description}</p>

            {/* Category Tabs */}
            <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-10">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    activeSlug === cat.slug
                      ? 'bg-amber-500 text-white'
                      : 'bg-background-50 text-foreground-700 border border-background-200/70 hover:border-amber-200/60'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Articles Grid */}
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.slug} {...article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-file-search-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无文章，请浏览其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}